import { useState } from "react";
import { useTheme } from "@mui/material";
import EnergySavingsLeafOutlinedIcon from "@mui/icons-material/EnergySavingsLeafOutlined";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSBox from "views/system-design/DSBox";
import DSStack from "views/system-design/DSStack";
import DSTypography from "views/system-design/DSTypography";
import DSTextField from "views/system-design/DSTextField";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import useEcoModeStats from "components/hooks/useEcoModeStats";

const flatCardSx = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "surface.card",
  backgroundImage: "none",
  borderRadius: "20px",
} as const;

/**
 * First clock version that ships ECO mode (clock-application-v3 release/624.3).
 * Clocks below it have no ECO code, so counting them would drown the real split -
 * the toggle defaults to ON, so every legacy clock would read as "on".
 */
const ECO_MIN_VERSION = 624.3;

const ON_COLOR = "#4CAF50";
const OFF_COLOR = "#607D8B";
const NOW_COLOR = "#2196F3";

function EcoModeWidget() {
  const theme = useTheme();
  const [fromVersion, setFromVersion] = useState<number>(ECO_MIN_VERSION);
  const { data, isLoading, error } = useEcoModeStats(fromVersion);

  const total = data?.total ?? 0;
  const pct = (count: number) =>
    total > 0 ? Math.round((count / total) * 100) : 0;
  const pctLabel = (count: number) => {
    if (count === 0) return "0%";
    const value = pct(count);
    return value === 0 ? "<1%" : `${value}%`;
  };

  const rows = data
    ? [
        { label: "Setting on", count: data.enabled, color: ON_COLOR },
        { label: "Setting off", count: data.disabled, color: OFF_COLOR },
        { label: "In ECO right now", count: data.inEcoNow, color: NOW_COLOR },
      ]
    : [];

  return (
    <DSCard sx={flatCardSx}>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          pt: 2,
          gap: 1,
        }}
      >
        <DSStack direction="row" alignItems="center" gap={1}>
          <DSBox
            sx={{
              color: "surface.accentText",
              "& svg": { fontSize: "1.5rem" },
              display: "flex",
            }}
          >
            <EnergySavingsLeafOutlinedIcon />
          </DSBox>
          <DSTypography variant="h6" sx={{ textTransform: "none" }}>
            ECO Mode
          </DSTypography>
        </DSStack>
        <DSStack direction="row" alignItems="center" spacing={1}>
          <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
            V&ge;
          </DSTypography>
          <DSTextField
            type="number"
            size="small"
            value={fromVersion}
            onChange={(e) =>
              setFromVersion(Number(e.target.value) || ECO_MIN_VERSION)
            }
            sx={{ width: 100 }}
          />
        </DSStack>
      </DSBox>
      <DSCardContent sx={{ flex: 1 }}>
        {isLoading ? (
          <DSBox sx={{ textAlign: "center", p: "20px" }}>
            <DSCircularProgress />
          </DSBox>
        ) : error || !data ? (
          <DSTypography variant="body2" sx={{ color: "error.main" }}>
            Failed to load
          </DSTypography>
        ) : total === 0 ? (
          <DSTypography variant="body2" sx={{ color: "text.secondary" }}>
            No clocks on V&ge;{fromVersion}
          </DSTypography>
        ) : (
          <>
            <DSBox sx={{ typography: "h6", fontFamily: "Gilroy-Bold" }}>
              {total.toLocaleString()}
            </DSBox>
            <DSTypography
              variant="body2"
              sx={{ color: "text.secondary", fontSize: 12, mb: 1.5 }}
            >
              ECO-capable clocks
            </DSTypography>
            {rows.map((row) => (
              <DSBox key={row.label} sx={{ mb: "6px" }}>
                <DSBox
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 11,
                    mb: "1px",
                  }}
                >
                  <span>{row.label}</span>
                  <span style={{ color: theme.palette.text.secondary }}>
                    {row.count} ({pctLabel(row.count)})
                  </span>
                </DSBox>
                <DSBox
                  sx={{
                    height: "6px",
                    backgroundColor: "white.12",
                    borderRadius: "3px",
                    overflow: "hidden",
                  }}
                >
                  <DSBox
                    sx={{
                      height: "100%",
                      width: `${row.count > 0 ? Math.max(pct(row.count), 1) : 0}%`,
                      backgroundColor: row.color,
                      borderRadius: "3px",
                    }}
                  />
                </DSBox>
              </DSBox>
            ))}
            {!data.settingReported && (
              <DSTypography
                variant="body2"
                sx={{ color: "text.secondary", fontSize: 11, mt: 1 }}
              >
                No clock has toggled the setting yet - all are on the ON
                default.
              </DSTypography>
            )}
          </>
        )}
      </DSCardContent>
    </DSCard>
  );
}

export default EcoModeWidget;
